import { Box, Stack, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

import type { MarginBuffer } from "@/data/mock-balances";

// ---- Component -----------------------------------------------------------

interface MarginBufferBarProps {
  buffer: MarginBuffer;
  /** Compact variant — half-height, no header text. Used inside drawers. */
  dense?: boolean;
}

/**
 * Robinhood-style horizontal progress bar showing how much of the
 * margin buffer is in use. The bar fills from the left as `usageRatio`
 * climbs from 0 → 1 and transitions through three states:
 *
 *   • `usageRatio < 0.5`           → green ("Ready to use")
 *   • `0.5 ≤ usageRatio < 0.8`     → amber ("Approaching limit")
 *   • `usageRatio ≥ 0.8`           → red   ("At margin call risk")
 *
 * The copy under the bar matches Robinhood's hierarchy — a big label
 * on the left summarizes state, the actual $ figures are surfaced as
 * two mini-stats on the right.
 */
export function MarginBufferBar({ buffer, dense = false }: MarginBufferBarProps) {
  const { usageRatio } = buffer;

  const state = usageRatio >= 0.8 ? "danger" : usageRatio >= 0.5 ? "warn" : "ok";
  const label =
    state === "ok"
      ? "Ready to use"
      : state === "warn"
        ? "Approaching limit"
        : "At margin call risk";

  const stateColor = (t: Theme): string => {
    switch (state) {
      case "ok":
        return t.palette.success.main;
      case "warn":
        return t.palette.warning.main;
      case "danger":
        return t.palette.error.main;
    }
  };

  // Fill width — floor at 6% so the user can still see a sliver even
  // when usage is near zero. Otherwise the bar reads as empty and the
  // "Ready to use" label has nothing to sit next to.
  const fillPct = Math.max(6, Math.round(usageRatio * 100));

  return (
    <Box>
      {!dense && (
        <Stack
          direction="row"
          alignItems="baseline"
          justifyContent="space-between"
          sx={{ mb: 0.75 }}
        >
          <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: 0.5 }}>
            Margin buffer
          </Typography>
          <Typography
            variant="body2"
            sx={(t) => ({
              fontWeight: 700,
              color: stateColor(t)
            })}
          >
            {label}
          </Typography>
        </Stack>
      )}

      <Box
        role="progressbar"
        aria-valuenow={Math.round(usageRatio * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Margin buffer usage — ${label}`}
        sx={(t) => ({
          position: "relative",
          height: dense ? 8 : 14,
          borderRadius: 999,
          bgcolor: alpha(stateColor(t), 0.18),
          overflow: "hidden"
        })}
      >
        <Box
          sx={(t) => ({
            position: "absolute",
            inset: 0,
            right: "auto",
            width: `${fillPct}%`,
            bgcolor: stateColor(t),
            borderRadius: 999,
            transition: "width 240ms ease, background-color 240ms ease"
          })}
        />
      </Box>

      {/* Mini-stats row removed — Portfolio value / Total maint req /
       *  Margin used / Available all duplicated other surfaces on the
       *  page (hero equity, "Maintenance Margin" row, |negative cash|,
       *  the headline "Buying Power"). The bar + state label tell the
       *  user everything they need without restating numbers visible
       *  half a screen away. (Sergey 2026-04-28.) */}
    </Box>
  );
}
